import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import chunk from 'lodash/chunk';

import { fetchExtraCardPanelHits } from 'Redux/ducks/algolia-calls';
import CardPanelCarousel from 'Components/Carousel/CardPanelCarousel/CardPanelCarousel';
import ErrorBoundary from 'Components/Utility/ErrorBoundary';
import useViewport from 'Hooks/useViewport';
import useFetchCarouselSlides from 'Components/Carousel/hooks/useFetchCarouselSlides';

export default function FeaturedCarouselContainer({ fantasyVerse, isPreview }) {
  const [extraSlides, setExtraSlides] = useState([]);
  const [slides, setSlides] = useState([]);
  const [loadedData, setLoadedData] = useState(false);

  const getSlideType = (hit) => {
    const isPreOrder = (hit.release_date * 1000) > Date.now() && !hit.presale;
    let slideType = hit.type === 'bundle' || hit.type === 'pick-and-mix' ? 'bundle' : 'product';

    if (hit.star_deal) slideType = 'starDeal';
    if (hit.flash_sale && hit.discount_percent > 0) slideType = 'flashDeal';
    if (isPreOrder) slideType = 'preOrder';

    return slideType;
  };

  const { isAboveLg, isBelowXl } = useViewport();

  let chunkNumber = 3;
  if (isAboveLg) chunkNumber = 5;

  const { apiResults } = useFetchCarouselSlides({ fantasyVerse, isPreview });

  // For the CardPanelCarousel, each slide is a group of 3 or 5 products.
  // If the number of slides is not divisible by 3 or 5, we need to fetch extra products to fill the gaps
  // If the screen is above LG, we need to fetch 5 products per slide, otherwise 3
  useEffect(() => {
    if (!isEmpty(apiResults) && Array.isArray(apiResults)) {
      // Promoted slides are larger and show on their own. This should be taken into account when getting extra products to fill the gaps
      const nonPromotedSlides = apiResults.filter(slide => !slide.promoted);
      const promotedSlides = apiResults.filter(slide => slide.promoted);

      const leftOverSlides = nonPromotedSlides.length % chunkNumber;

      // If there are no leftover slides and no promoted slides, we don't need any extra slides
      if (leftOverSlides === 0 && isEmpty(promotedSlides)) setLoadedData(true);
      // If there are no leftover slides and there are promoted slides but above LG, we don't need any extra slides
      if (leftOverSlides === 0 && !isEmpty(promotedSlides) && isAboveLg) setLoadedData(true);

      // Find out how many extra slides we need to get
      let numberOfExtraHits = 0;
      if (leftOverSlides > 0) {
        numberOfExtraHits = chunkNumber - leftOverSlides;
      }

      // The layout on LG and below is one large card with two smaller cards below. If there is a promoted slide, we need to fetch 2 extra hits to show next to it
      if (!isEmpty(promotedSlides) && isBelowXl) {
        if (leftOverSlides > 0) {
          numberOfExtraHits = (chunkNumber - leftOverSlides) + (2 * promotedSlides.length);
        } else {
          numberOfExtraHits = 2 * promotedSlides.length;
        }
      }

      // Get extra slides if they are needed. Don't get any products we already have
      if (numberOfExtraHits > 0) {
        const slugsToExclude = [];

        apiResults.forEach((slide) => {
          if ((slide.type === 'product' || slide.type === 'bundle' || slide.type === 'starDeal' || slide.type === 'preOrder' || slide.type === 'flashDeal') && slide.hit) {
            slugsToExclude.push(slide.hit.slug);
          }
        });

        const fetchExtraHits = async () => {
          const hits = await fetchExtraCardPanelHits(
            numberOfExtraHits,
            slugsToExclude,
            fantasyVerse,
          );


          const formattedSlides = [];
          if (!isEmpty(hits)) {
            hits.forEach((hit) => {
              formattedSlides.push({
                _id: hit.product_id,
                img: hit.cover,
                hit,
                name: hit.name,
                type: getSlideType(hit),
                url: `https://www.fanatical.com/en/${hit.type}/${hit.slug}`,
                external_link: false,
                sort_order: null,
              });
            });
          }
          setExtraSlides(formattedSlides);
          setLoadedData(true);
        };

        fetchExtraHits();
      }
    }
  }, [apiResults, chunkNumber, fantasyVerse, isBelowXl, isAboveLg]);

  // Chunk up the slides for the card panel carousel
  useEffect(() => {
    if (!isEmpty(apiResults) && loadedData) {
      const promotedSlides = apiResults.filter(slide => slide.promoted);
      const nonPromotedSlides = apiResults.filter(slide => !slide.promoted);

      // Sort all non-promoted slides by their sort_order
      const sortedSlides = nonPromotedSlides.sort((a, b) => {
        if (a.showFirst !== b.showFirst) {
          return a.showFirst ? -1 : 1;
        }
        return a.sort_order - b.sort_order;
      });


      let extraSlidesForPromoted = [];
      let allSlides = sortedSlides;
      // If there are any extra slides add them onto the end of allSlides
      if (!isEmpty(extraSlides)) {
        allSlides = sortedSlides.concat(extraSlides);
      }
      // If there are promoted slides, on any breakpoint below XL we need to reserve 2 extra slides per promoted slide to display next to it
      if (!isEmpty(promotedSlides) && isBelowXl) {
        extraSlidesForPromoted = allSlides.slice(allSlides.length - (2 * promotedSlides.length));
        allSlides = allSlides.slice(0, allSlides.length - (2 * promotedSlides.length));
      }
      // Chunk up the slides by the chunk number
      const carouselChunks = chunk(allSlides, chunkNumber);
      // If there are any promoted slides, these are inserted into the second slot in the array
      if (!isEmpty(promotedSlides)) {
        promotedSlides.forEach((promotedSlide) => {
          carouselChunks.splice(1, 0, [promotedSlide]); // splice(position, numberOfItemsToRemove, itemToAdd)
        });
      }
      // Once the promoted slides are back in the chunks, below XL 2 reserved slides need to be inserted with every promoted slide
      if (!isEmpty(promotedSlides) && isBelowXl) {
        carouselChunks.forEach((carouselChunk, index) => {
          if (carouselChunk.length === 1 && carouselChunk[0].promoted) {
            const firstExtraSlide = extraSlidesForPromoted[(index - 1) * 2] || {}; // index - 1 because the first chunk is not promoted. * 2 to get 2 slides
            const secondExtraSlide = extraSlidesForPromoted[(index - 1) * 2 + 1] || {}; // index - 1 because the first chunk is not promoted. * 2 to get 2 slides
            carouselChunk.push(firstExtraSlide, secondExtraSlide);
          }
        });
      }
      // Add a slide number to each slide once they are all together
      let counter = 1;
      carouselChunks.forEach((group) => {
        group.forEach((slide) => {
          slide.slideNumber = counter;
          counter += 1;
        });
      });

      if (loadedData) setSlides(carouselChunks);
    }
  }, [apiResults, chunkNumber, extraSlides, loadedData, isBelowXl]);

  return (
    <ErrorBoundary hideForm>
      <CardPanelCarousel
        slides={slides}
        fantasyVerse={fantasyVerse}
      />
    </ErrorBoundary>
  );
}

FeaturedCarouselContainer.propTypes = {
  fantasyVerse: PropTypes.bool,
  isPreview: PropTypes.bool,
};

FeaturedCarouselContainer.defaultProps = {
  fantasyVerse: false,
  isPreview: false,
};
